import { useEffect, useRef, useState, type DragEvent } from 'react';
import { FileIcon } from './Icons';

interface DropZoneProps {
  accepted: string[];
  maxUploadMb: number;
  officeConversion: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
  /** Exposes the hidden file input so the toolbar's "Add files" can open it too. */
  registerOpen: (open: () => void) => void;
}

export function DropZone({
  accepted,
  maxUploadMb,
  officeConversion,
  disabled,
  onFiles,
  registerOpen,
}: DropZoneProps) {
  const [active, setActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    registerOpen(() => inputRef.current?.click());
  }, [registerOpen]);

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setActive(false);
    if (disabled) return;
    const files = [...event.dataTransfer.files];
    if (files.length > 0) onFiles(files);
  }

  return (
    <div
      className={active ? 'dropzone dropzone--active' : 'dropzone'}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && !disabled) inputRef.current?.click();
      }}
    >
      <FileIcon size={22} />
      <p>
        <strong>Drop files here</strong> or click to browse
      </p>
      <p style={{ fontSize: 12 }}>
        PDF, JPG, PNG{officeConversion ? ', Word, Excel, PowerPoint' : ''} · up to {maxUploadMb} MB each
      </p>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        accept={accepted.map((extension) => `.${extension}`).join(',')}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length > 0) onFiles(files);
          // Reset so re-selecting the same file fires a change event again.
          event.target.value = '';
        }}
      />
    </div>
  );
}
